import type { PermissionTuple } from '~/utils/ability'
import type { PermissionRequirement, RouteTo } from '~/utils/rbac'
import { moduleIndexPermission } from '~/utils/rbacModuleIndex'
import type { GuardedTab } from '~/utils/rbacTabs'

/** Chaque onglet est une URL distincte : l'onglet courant est déduit de la route active. */
export type SellersTab = 'sellers' | 'establishments'

/**
 * Source unique des droits de lecture des sous-onglets Vendeurs.
 *
 * Consommée par le `staticData.permission` de chaque route d'onglet, par la barre d'onglets, et par
 * `/sellers` qui n'est accessible que si l'un d'eux l'est.
 */
export const SELLERS_TAB_PERMISSIONS = {
  sellers: ['read', 'sellers.sellers.seller'],
  establishments: ['read', 'sellers.establishments.establishment'],
} as const satisfies Record<SellersTab, PermissionTuple>

/** Chaque onglet a forcément une route et un droit de lecture — d'où les champs rendus requis. */
type SellersTabDefinition = GuardedTab<SellersTab> & {
  to: RouteTo
  permission: PermissionTuple
}

/** Ordre de déclaration = ordre d'affichage, et ordre de repli de la redirection `/sellers`. */
export const SELLERS_TABS: SellersTabDefinition[] = [
  {
    value: 'sellers',
    label: 'Vendeurs',
    to: '/sellers/sellers',
    permission: SELLERS_TAB_PERMISSIONS.sellers,
  },
  {
    value: 'establishments',
    label: 'Établissements',
    to: '/sellers/establishments',
    permission: SELLERS_TAB_PERMISSIONS.establishments,
  },
]

/**
 * Accès au module : dès qu'un seul de ses écrans est accessible — les créations et éditions de
 * vendeur et d'établissement comprises (audit du 19/09, MAJ-07).
 */
export const SELLERS_PAGE_READ: PermissionRequirement = moduleIndexPermission([
  ...SELLERS_TABS.map((tab) => tab.permission),
  ['add', 'sellers.sellers.seller'],
  ['edit', 'sellers.sellers.seller'],
  ['add', 'sellers.establishments.establishment'],
  ['edit', 'sellers.establishments.establishment'],
])
